import {
  forwardRef,
  ForwardRefExoticComponent,
  InputHTMLAttributes,
  Ref,
  RefAttributes,
} from 'react';
import styled, { css } from 'styled-components';
import isEqual from 'lodash/isEqual';

import { variableFontMedium, variableFontRegular } from 'styles/theme/text';

interface IInputStatic {
  Content: any;
  Error: any;
  Input: any;
  Label: any;
  Wrapper: any;
}
interface IAllInputProps extends InputHTMLAttributes<HTMLInputElement> {
  'data-qa': string;
  className?: string;
  errorMessage?: string | boolean;
  hasError?: boolean;
  hasFloatingLabel?: boolean;
  id?: string;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  label?: string;
  name: string;
}

type InputComponent = ForwardRefExoticComponent<IInputProps & RefAttributes<HTMLInputElement>> &
  IInputStatic;

export type IInputProps = IAllInputProps;

const ensureEmptyStringIfNil = (param) => {
  if (isEqual(param, 0)) {
    return '0';
  }

  return param || '';
};

export const Input = forwardRef(
  (
    {
      errorMessage,
      className,
      hasError,
      hasFloatingLabel = true,
      label,
      name,
      value,
      ...props
    }: IInputProps,
    ref?: Ref<HTMLInputElement>,
  ) => {
    return (
      <Input.Content className={className} $hasError={hasError}>
        <Input.Wrapper>
          <Input.Input
            id={name}
            name={name}
            placeholder={hasFloatingLabel ? '' : label}
            ref={ref}
            value={ensureEmptyStringIfNil(value)}
            {...props}
          />
          {hasFloatingLabel && label && (
            <Input.Label hasFloatingLabel={hasFloatingLabel} hasValue={!!value} for={name}>
              {label}
            </Input.Label>
          )}
          {!hasFloatingLabel && (
            <Input.Label hasFloatingLabel={hasFloatingLabel} hasValue={!!value} for={name}>
              {name}
            </Input.Label>
          )}
        </Input.Wrapper>
        {hasError && (
          <Input.Error data-qa={`${props['data-qa']}_error`} className="inputError">
            {errorMessage}
          </Input.Error>
        )}
      </Input.Content>
    );
  },
) as InputComponent;

Input.Content = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

Input.Wrapper = styled.div`
  position: relative;
`;

Input.Input = styled.input`
  display: block;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
  border-radius: 0;
  padding: 4px 0 8px;
  width: 100%;
  ${variableFontMedium({ size: '11px', height: '14px', fontWeight: '400' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontMedium({ size: '11.5px', height: '14px', fontWeight: '400' })};
  }
  &:focus {
    outline: none;
    border-bottom-width: 2px;
    /* this ensures the height does not change when we increase border-bottom */
    padding-bottom: 7px;
    ${variableFontMedium({ size: '13px', height: '14px', fontWeight: '400' })};

    @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
      ${variableFontMedium({ size: '13.5px', height: '14px', fontWeight: '400' })};
    }
  }
`;

const activeStyle = css`
  color: inherit;
  transform: translateY(-18px);
  ${variableFontMedium({ size: '9px', height: '14px', fontWeight: '400' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontMedium({ size: '9.5px', height: '14px', fontWeight: '400' })};
  }
`;

// Input.Label represents the input placeholder. The placeholder animates/moves above
// the input when the input is in focus or contains a value
Input.Label = styled.label<{ hasFloatingLabel: boolean; hasValue: boolean }>`
  position: absolute;
  pointer-events: none;
  transition: 0.2s ease all;
  left: 0;
  bottom: 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
  visibility: ${({ hasFloatingLabel }) => (hasFloatingLabel ? 'visible' : 'hidden')};

  ${({ hasValue }) => hasValue && activeStyle};

  ${Input.Input}:focus ~ & {
    ${activeStyle};
  }
`;

Input.Error = styled.div`
  color: ${({ theme }) => theme.colors.error};
  ${variableFontRegular({ size: '9.5px', height: '9px' })};
  margin-top: 8px;
`;
